'use client'

import type { MultiCodeVariant } from '@common/components/MultiCodeSnippet/MultiCodeSnippet'
import { MultiCodeSnippet } from '@common/components/MultiCodeSnippet/MultiCodeSnippet'

import { AssetSource } from '@/modules/academy/enums'

import { Collapsible } from '../components/Collapsible/Collapsible'
import { InfoBox } from '../InfoBox'
import type { MarkdownComponents } from '../MarkdownRenderer.types'

import { transformImagePaths } from './transformImagePaths'

import styles from '../MarkdownRenderer.module.scss'

type CreateMarkdownDivHandlerParams = {
  assetSource: AssetSource
  collapsibles: Map<string, string>
  infoBoxes: Map<string, { type: 'tip' | 'note' | 'warning'; content: string }>
  multiCodeBlocks: Map<string, MultiCodeVariant[]>
  markdownComponents: MarkdownComponents
}

const resolveClassName = (nodeProps: Record<string, unknown>, propsClassName?: unknown): string => {
  if (nodeProps.className) {
    if (typeof nodeProps.className === 'string') {
      return nodeProps.className
    }
    if (Array.isArray(nodeProps.className)) {
      return nodeProps.className.join(' ')
    }
    if (typeof nodeProps.className === 'object') {
      return Object.keys(nodeProps.className as Record<string, boolean>)
        .filter((key) => (nodeProps.className as Record<string, boolean>)[key])
        .join(' ')
    }
  }

  if (nodeProps.class) {
    return typeof nodeProps.class === 'string' ? nodeProps.class : String(nodeProps.class)
  }

  if (propsClassName) {
    return typeof propsClassName === 'string' ? propsClassName : String(propsClassName)
  }

  return ''
}

export function createMarkdownDivHandler({
  assetSource,
  collapsibles,
  infoBoxes,
  multiCodeBlocks,
  markdownComponents,
}: CreateMarkdownDivHandlerParams) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (props: any) => {
    const nodeProps = props.node?.properties || {}
    const className = resolveClassName(nodeProps, props.className)

    if (className.includes('multi-code-wrapper')) {
      const contentId = nodeProps['data-content-id'] || nodeProps['dataContentId'] || props['data-content-id'] || ''
      const variants = multiCodeBlocks.get(contentId)

      if (variants && variants.length > 0) {
        return (
          <div className={styles.codeSnippetWrapper}>
            <MultiCodeSnippet variants={variants} />
          </div>
        )
      }
    }

    if (className.includes('collapsible-wrapper')) {
      const title =
        nodeProps['data-title'] ||
        nodeProps['dataTitle'] ||
        (nodeProps['data-title'] !== undefined ? String(nodeProps['data-title']) : '') ||
        props['data-title'] ||
        ''

      const contentId = nodeProps['data-content-id'] || nodeProps['dataContentId'] || props['data-content-id'] || ''

      const decodedTitle =
        typeof title === 'string'
          ? title
              .replace(/&amp;/g, '&')
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
          : ''

      const rawCollapsibleContent = collapsibles.get(contentId) || ''
      const markdownContent = transformImagePaths(rawCollapsibleContent, assetSource)

      return (
        <Collapsible
          title={decodedTitle}
          content={markdownContent}
          markdownComponents={markdownComponents}
          contentId={contentId}
        />
      )
    }

    if (className.includes('info-box-wrapper')) {
      const contentId = nodeProps['data-content-id'] || nodeProps['dataContentId'] || props['data-content-id'] || ''
      const infoBoxData = infoBoxes.get(contentId)

      if (infoBoxData) {
        const transformedInfoContent = transformImagePaths(infoBoxData.content, assetSource)
        return (
          <InfoBox type={infoBoxData.type} content={transformedInfoContent} markdownComponents={markdownComponents} />
        )
      }
    }

    const { node: _node, ...restProps } = props
    return <div {...restProps} />
  }
}
